import type { AccessSession, AuthSession, PublicUser } from "@fortress/shared";
import { httpBase } from "../net/origin";

// REST calls to the server's /auth endpoints, over the shared HTTP origin
// (see net/origin.ts). Authenticated calls carry ONLY the bearer JWT; the
// refresh token travels exclusively to `/auth/refresh` (rotation) and
// `/auth/logout` (revocation).

/** The server rejected the bearer JWT (401) — refresh and retry. */
export class UnauthorizedError extends Error {
  constructor(message = "unauthorized") {
    super(message);
    this.name = "UnauthorizedError";
  }
}

/**
 * A non-ok HTTP response, carrying the status so callers can tell a definitive
 * rejection (4xx — the server understood and refused) from a transient failure
 * (5xx; network errors surface as fetch's own TypeError instead).
 */
export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function postJson<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(`${httpBase()}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const detail = await res.json().catch(() => ({}));
    throw new ApiError(
      res.status,
      (detail as { error?: string }).error ?? `HTTP ${res.status}`,
    );
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

/** Exchange a Google ID token (credential) for a server session. */
export function loginWithGoogle(idToken: string): Promise<AuthSession> {
  return postJson<AuthSession>("/auth/google", { idToken });
}

/** Request a one-time login code emailed to `email`. */
export function requestEmailOtp(email: string): Promise<void> {
  return postJson<void>("/auth/email/request", { email });
}

/** Exchange an email + OTP code for a server session. */
export function loginWithEmail(email: string, code: string): Promise<AuthSession> {
  return postJson<AuthSession>("/auth/email/verify", { email, code });
}

/** QA login (non-prod only): exchange a bare email for a session, no OTP. */
export function qaLogin(email: string): Promise<AuthSession> {
  return postJson<AuthSession>("/auth/email/qa-login", { email });
}

/** Rotate the refresh token and get a fresh access token. */
export function refreshSession(refreshToken: string): Promise<AuthSession> {
  return postJson<AuthSession>("/auth/refresh", { refreshToken });
}

/**
 * Current account for an access token (`GET /auth/me`). Throws
 * `UnauthorizedError` when the JWT is rejected (expired/invalid) so the caller
 * can refresh and retry; returns null ONLY when the server definitively says
 * the account is gone (404). Other failures (5xx, network) throw — they say
 * nothing about the session's validity.
 */
export async function fetchMe(accessToken: string): Promise<PublicUser | null> {
  const res = await fetch(`${httpBase()}/auth/me`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (res.status === 401) throw new UnauthorizedError();
  if (res.status === 404) return null;
  if (!res.ok) throw new ApiError(res.status, `HTTP ${res.status}`);
  const body = (await res.json()) as { user?: PublicUser };
  return body.user ?? null;
}

/** Revoke the device's session server-side. Best-effort; never throws. */
export async function logout(refreshToken: string): Promise<void> {
  try {
    await postJson("/auth/logout", { refreshToken });
  } catch {
    // ignore — local tokens are cleared regardless by the caller
  }
}

/**
 * Set the account username once; returns a fresh access-only session (the
 * refresh token stays as-is). Throws `UnauthorizedError` on a rejected JWT so
 * the caller can refresh and retry.
 */
export async function setUsername(
  username: string,
  accessToken: string,
): Promise<AccessSession> {
  const res = await fetch(`${httpBase()}/auth/username`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify({ username }),
  });
  if (res.status === 401) throw new UnauthorizedError();
  if (!res.ok) {
    const detail = await res.json().catch(() => ({}));
    throw new Error((detail as { error?: string }).error ?? `HTTP ${res.status}`);
  }
  return res.json() as Promise<AccessSession>;
}
